import React, { useCallback } from 'react';
import { Task } from '../../types';
import { dateFromLocalKey } from './dateUtils';
import { PlannerData } from './usePlannerData';
import { getTaskDateUpdates } from './taskReschedule';
import { getTaskEnd, getTaskStartDate, isHourlySchedule } from './scheduleUtils';
import { PlannerRefs } from './usePlannerRefs';
import { PlannerState } from './usePlannerState';

export const useTaskGestures = (state: PlannerState, refs: PlannerRefs, data: PlannerData) => {
    const {
        dragTaskStartTopY, dragTaskStartY, draggingTask, resizeStartDuration, resizeStartY,
        resizingTask, showSchedule, timelineScale, timeSlotHeight, setCurrentMouseY,
        setDraftInteraction, setDraftSelection, setDragTaskScrollTop, setDragTaskStartTopY, setDragTaskStartY,
        setDraggingTask, setResizeStartDuration, setResizeStartY, setResizingTask,
    } = state;
    const { timelineRef } = refs;
    const { updateTask } = data;
    const getTimelineDateFromClientY = useCallback((clientY: number) => {
        const slots = Array.from(timelineRef.current?.querySelectorAll(
            '.timeline-slot[data-timeline-date][data-timeline-hour]'
        ) || []) as HTMLElement[];
        for (const [index, slot] of slots.entries()) {
            const rect = slot.getBoundingClientRect();
            if (clientY >= rect.top && (clientY < rect.bottom || (index === slots.length - 1 && clientY <= rect.bottom))) {
                const key = slot.dataset.timelineDate;
                const hour = Number(slot.dataset.timelineHour);
                if (!key || Number.isNaN(hour)) return null;
                const minutes = Math.min(45, Math.max(0, Math.round((clientY - rect.top) / (rect.height / 60) / 15) * 15));
                const date = dateFromLocalKey(key);
                date.setHours(hour, minutes, 0, 0);
                return date;
            }
        }
        return null;
    }, [timelineRef]);
    const startTaskDrag = (task: Task, collectionId: string, element: HTMLElement, clientY: number, pointerId: number) => {
        element.setPointerCapture?.(pointerId);
        setDraftSelection(null);
        setDraftInteraction(null);
        setDraggingTask({ task, collectionId });
        setDragTaskStartY(clientY);
        setDragTaskStartTopY(element.getBoundingClientRect().top);
        const scrollTop = timelineRef.current?.scrollTop || 0;
        setDragTaskScrollTop(scrollTop);
    };
    const handleTaskLongPress = (task: Task, collectionId: string, element: HTMLElement, clientY: number, pointerId: number) =>
        startTaskDrag(task, collectionId, element, clientY, pointerId);
    const handleTaskPointerMove = useCallback((event: PointerEvent | React.PointerEvent) => {
        if (draggingTask && dragTaskStartY !== null) setCurrentMouseY(event.clientY);
    }, [dragTaskStartY, draggingTask, setCurrentMouseY]);
    const handleTaskPointerUp = useCallback((event: PointerEvent | React.PointerEvent) => {
        if (!draggingTask || dragTaskStartY === null) return;
        const deltaY = event.clientY - dragTaskStartY;
        const deltaMinutes = Math.round(deltaY / (timeSlotHeight / 60) / 15) * 15;
        if (Math.abs(deltaY) >= 5) {
            if (!showSchedule && timelineScale === 'day' && dragTaskStartTopY !== null) {
                const taskTopY = dragTaskStartTopY + event.clientY - dragTaskStartY;
                const target = getTimelineDateFromClientY(taskTopY);
                if (target) updateTask(draggingTask.collectionId, draggingTask.task.id, getTaskDateUpdates(draggingTask.task, target));
            } else if (Math.abs(deltaMinutes) >= 15) {
                const date = getTaskStartDate(draggingTask.task);
                if (!date) return;
                date.setMinutes(date.getMinutes() + deltaMinutes);
                updateTask(draggingTask.collectionId, draggingTask.task.id, getTaskDateUpdates(draggingTask.task, date));
            }
        }
        setDraggingTask(null);
        setDragTaskScrollTop(0);
        setDragTaskStartY(null);
        setDragTaskStartTopY(null);
        setCurrentMouseY(null);
    }, [dragTaskStartTopY, dragTaskStartY, draggingTask, getTimelineDateFromClientY,
        setCurrentMouseY, setDragTaskScrollTop, setDragTaskStartTopY, setDragTaskStartY, setDraggingTask,
        showSchedule, timeSlotHeight, timelineScale, updateTask]);
    const handleResizeLongPress = (task: Task, collectionId: string, edge: 'top' | 'bottom', element: HTMLElement, clientY: number, pointerId: number) => {
        element.setPointerCapture?.(pointerId);
        setResizingTask({ task, collectionId, edge });
        setResizeStartY(clientY);
        const start = getTaskStartDate(task);
        if (!start) return;
        setResizeStartDuration(Math.max(15, Math.round((getTaskEnd(task).getTime() - start.getTime()) / 60_000)));
        setCurrentMouseY(clientY);
    };
    const handleResizePointerMove = useCallback((event: PointerEvent | React.PointerEvent) => {
        if (resizingTask && resizeStartY !== null) setCurrentMouseY(event.clientY);
    }, [resizeStartY, resizingTask, setCurrentMouseY]);
    const handleResizePointerUp = useCallback((event: PointerEvent | React.PointerEvent) => {
        if (!resizingTask || resizeStartY === null) return;
        let delta = Math.round((event.clientY - resizeStartY) / (timeSlotHeight / 60) / 15) * 15;
        if (resizingTask.edge === 'top') delta = -delta;
        const duration = Math.max(15, resizeStartDuration + delta);
        if (duration !== resizeStartDuration) {
            const start = getTaskStartDate(resizingTask.task);
            if (!start) return;
            const updates: Partial<Task> = isHourlySchedule(resizingTask.task)
                ? {
                    schedule: {
                        precision: 'hour',
                        kind: 'range',
                        startDate: start.toISOString(),
                        endDate: new Date(start.getTime() + duration * 60_000).toISOString(),
                    },
                }
                : { duration };
            if (resizingTask.edge === 'top' && delta !== 0) {
                const date = new Date(start);
                date.setMinutes(date.getMinutes() - delta);
                updates.dueDate = date.toISOString();
                if (isHourlySchedule(resizingTask.task)) {
                    updates.schedule = {
                        precision: 'hour',
                        kind: 'range',
                        startDate: date.toISOString(),
                        endDate: getTaskEnd(resizingTask.task).toISOString(),
                    };
                }
            }
            updateTask(resizingTask.collectionId, resizingTask.task.id, updates);
        }
        setResizingTask(null);
        setResizeStartY(null);
        setCurrentMouseY(null);
    }, [resizeStartDuration, resizeStartY, resizingTask, setCurrentMouseY,
        setResizeStartY, setResizingTask, timeSlotHeight, updateTask]);
    return {
        getTimelineDateFromClientY, handleResizeLongPress, handleResizePointerMove,
        handleResizePointerUp, handleTaskLongPress, handleTaskPointerMove, handleTaskPointerUp,
    };
};
